/**
 * Dashboard tile — surfaces the nearest active contest on the artist
 * dashboard. Self-hides when the Contest Center flag is OFF or no
 * open/upcoming contest exists.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import ContestStatusBadge from "./ContestStatusBadge";
import ContestCountdown from "./ContestCountdown";
import { contestCountdownTarget, deriveContestDisplayStatus } from "@/lib/contestStatus";

export const DashboardContestTile = () => {
  const [enabled, setEnabled] = useState(null);
  const [contest, setContest] = useState(null);
  const [myEntryCount, setMyEntryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/contests");
        if (cancelled) return;
        if (r.data?.enabled === false) {
          setEnabled(false);
          return;
        }
        setEnabled(true);
        const rows = (r.data?.contests || []).slice();
        // Rank: open first, then coming_soon, then judging.
        const rank = { open: 0, coming_soon: 1, judging: 2, deadline_passed: 3, closed: 4, draft: 9 };
        rows.sort((a, b) => {
          const ka = rank[deriveContestDisplayStatus(a).key] ?? 8;
          const kb = rank[deriveContestDisplayStatus(b).key] ?? 8;
          return ka - kb;
        });
        setContest(rows[0] || null);
        try {
          const me = await api.get("/contests/me/entries");
          if (!cancelled) setMyEntryCount((me.data?.entries || []).length);
        } catch {
          if (!cancelled) setMyEntryCount(0);
        }
      } catch {
        if (!cancelled) setEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (enabled === false || !contest) return null;

  const cd = contestCountdownTarget(contest);

  return (
    <Card
      className="p-5 bg-gradient-to-br from-amber-900/30 via-purple-900/20 to-black border-amber-500/30 hover:border-amber-500/60 transition-all"
      data-testid="dashboard-contest-tile"
    >
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🏆 RGM Contest Center</p>
          <h3 className="text-lg font-black text-white leading-tight mt-0.5" data-testid="dashboard-contest-tile-title">
            {contest.title}
          </h3>
        </div>
        <ContestStatusBadge contest={contest} />
      </div>
      {contest.description && (
        <p className="text-xs text-gray-400 line-clamp-2 mb-3">{contest.description}</p>
      )}
      {cd && (
        <div className="mb-3">
          <ContestCountdown targetIso={cd.target} label={cd.label} compact />
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/contests/${contest.slug}`}
          className="inline-flex items-center px-3 py-1.5 rounded-full bg-white text-black text-xs font-black hover:bg-amber-200 transition-colors"
          data-testid="dashboard-contest-tile-view"
        >
          View contest →
        </Link>
        <Link
          to="/contests/my"
          className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-white text-xs font-black hover:bg-white/20 transition-colors"
          data-testid="dashboard-contest-tile-mine"
        >
          My submissions ({myEntryCount})
        </Link>
      </div>
    </Card>
  );
};

export default DashboardContestTile;
